import { EPS } from './numbers.js';
import { distance, type Vec2 } from './vec.js';
import {
  distancePointToSegment,
  segmentsIntersect,
  segmentsProperlyIntersect,
  type Segment,
} from './segment.js';

/** Closed polygon given as vertex list without repeating the first vertex. */
export type Polygon = readonly Vec2[];

/** Shoelace formula. Positive for counter-clockwise vertex order. */
export function signedArea(poly: Polygon): number {
  const n = poly.length;
  if (n < 3) return 0;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const a = poly[i] as Vec2;
    const b = poly[(i + 1) % n] as Vec2;
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}

export const polygonArea = (poly: Polygon): number => Math.abs(signedArea(poly));

export const isCounterClockwise = (poly: Polygon): boolean => signedArea(poly) > 0;

export function ensureCounterClockwise(poly: Polygon): Vec2[] {
  return isCounterClockwise(poly) ? [...poly] : [...poly].reverse();
}

export function polygonEdges(poly: Polygon): Segment[] {
  const n = poly.length;
  const edges: Segment[] = [];
  for (let i = 0; i < n; i++) {
    edges.push({ a: poly[i] as Vec2, b: poly[(i + 1) % n] as Vec2 });
  }
  return edges;
}

export function edgeLengths(poly: Polygon): number[] {
  return polygonEdges(poly).map((e) => distance(e.a, e.b));
}

export function perimeter(poly: Polygon): number {
  return edgeLengths(poly).reduce((s, l) => s + l, 0);
}

/** Area centroid. Falls back to vertex average for degenerate polygons. */
export function polygonCentroid(poly: Polygon): Vec2 {
  const area = signedArea(poly);
  const n = poly.length;
  if (n === 0) return { x: 0, y: 0 };
  if (Math.abs(area) < EPS) {
    const sx = poly.reduce((s, p) => s + p.x, 0);
    const sy = poly.reduce((s, p) => s + p.y, 0);
    return { x: sx / n, y: sy / n };
  }
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < n; i++) {
    const a = poly[i] as Vec2;
    const b = poly[(i + 1) % n] as Vec2;
    const f = a.x * b.y - b.x * a.y;
    cx += (a.x + b.x) * f;
    cy += (a.y + b.y) * f;
  }
  return { x: cx / (6 * area), y: cy / (6 * area) };
}

/** Ray casting. Points exactly on the boundary may report either result. */
export function pointInPolygon(p: Vec2, poly: Polygon): boolean {
  let inside = false;
  const n = poly.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = poly[i] as Vec2;
    const b = poly[j] as Vec2;
    const intersects =
      a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

export function pointOnPolygonBoundary(p: Vec2, poly: Polygon, tolerance: number = EPS): boolean {
  return polygonEdges(poly).some((e) => distancePointToSegment(p, e) <= tolerance);
}

/** Inside or on the boundary. */
export function pointInOrOnPolygon(p: Vec2, poly: Polygon, tolerance: number = EPS): boolean {
  return pointOnPolygonBoundary(p, poly, tolerance) || pointInPolygon(p, poly);
}

/**
 * Containment test that also works for concave outer polygons (e.g. L-shaped sites):
 * every inner vertex and edge midpoint lies inside or on the boundary and no edges cross.
 * Shapes touching the boundary from inside count as contained.
 */
export function polygonContainsPolygon(
  outer: Polygon,
  inner: Polygon,
  tolerance: number = EPS,
): boolean {
  const innerEdges = polygonEdges(inner);
  for (const e of innerEdges) {
    if (!pointInOrOnPolygon(e.a, outer, tolerance)) return false;
    const mid = { x: (e.a.x + e.b.x) / 2, y: (e.a.y + e.b.y) / 2 };
    if (!pointInOrOnPolygon(mid, outer, tolerance)) return false;
  }
  const outerEdges = polygonEdges(outer);
  for (const ie of innerEdges) {
    for (const oe of outerEdges) {
      if (segmentsProperlyIntersect(ie, oe)) return false;
    }
  }
  return true;
}

/**
 * True if the interiors overlap. Shapes that only share edges or corners (e.g. two zones
 * placed side by side) do not overlap.
 */
export function polygonsOverlap(a: Polygon, b: Polygon, tolerance: number = EPS): boolean {
  const ea = polygonEdges(a);
  const eb = polygonEdges(b);
  for (const x of ea) for (const y of eb) if (segmentsProperlyIntersect(x, y)) return true;
  const strictlyInside = (p: Vec2, poly: Polygon) =>
    pointInPolygon(p, poly) && !pointOnPolygonBoundary(p, poly, tolerance);
  if (a.some((p) => strictlyInside(p, b)) || b.some((p) => strictlyInside(p, a))) return true;
  // Identical or edge-aligned shapes have no strictly-inside vertices; test their centroids.
  return strictlyInside(polygonCentroid(a), b) || strictlyInside(polygonCentroid(b), a);
}

/**
 * A polygon is simple if it has ≥ 3 vertices, non-zero area, no duplicate consecutive
 * vertices and no two non-adjacent edges touching. O(n²) is fine for planning polygons,
 * which rarely exceed a few dozen vertices.
 */
export function isSimplePolygon(poly: Polygon): boolean {
  const n = poly.length;
  if (n < 3) return false;
  if (polygonArea(poly) < EPS) return false;
  const edges = polygonEdges(poly);
  for (const e of edges) {
    if (distance(e.a, e.b) < EPS) return false;
  }
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const adjacent = j === i + 1 || (i === 0 && j === n - 1);
      if (adjacent) continue;
      if (segmentsIntersect(edges[i] as Segment, edges[j] as Segment)) return false;
    }
  }
  return true;
}

/** Axis-aligned rectangle with its minimum corner at `origin`, CCW order. */
export function rectanglePolygon(
  width: number,
  depth: number,
  origin: Vec2 = { x: 0, y: 0 },
): Vec2[] {
  return [
    { x: origin.x, y: origin.y },
    { x: origin.x + width, y: origin.y },
    { x: origin.x + width, y: origin.y + depth },
    { x: origin.x, y: origin.y + depth },
  ];
}
